<script lang="ts">
  import Icon from '$components/ui/icon/icon.svelte';
  import Divider from '$components/ui/divider.svelte';
  import { type Reviews } from '$lib/yotpo';
  import { t } from '$lib/i18n';
  import { convertDateToLocaleString } from '$lib/utils/date';
  import Carousel from '$components/carousel.svelte';
  import { cn } from '$lib/utils';
  import { cva } from 'class-variance-authority';
  import { drawer } from '$lib/stores/drawer';
  import { analyticsEvent, AnalyticsEvents } from '$lib/analytics';

  export let reviews: Reviews['reviews'] | undefined;
  export let productId: string;
  export let productTitle: string;
  export let productType: string;
  export let productVariant: string;
  let className: string | undefined;
  export { className as class };

  export let variant: 'default' | 'small' = 'default';

  const reviewCardVariants = cva('flex flex-col rounded-md bg-highlight', {
    variants: {
      variant: {
        default: 'justify-between p-3 mb-3',
        small: 'w-[280px] gap-[10px] p-[10px] cursor-pointer'
      }
    }
  });

  // Desgin req -> make the card clickable and scroll to the review
  // (cover cases when the review doesn't have enough space to be visible)
  // did not want to make the whole card a button because of the default variant (inside the drawer)
  const openDrawerAndScrollToReview = (productId: string, reviewId: string) => {
    analyticsEvent({
      event: AnalyticsEvents.OPEN_REVIEWS,
      items: [
        {
          item_id: productId,
          item_name: productTitle,
          item_category: productType,
          item_variant: productVariant
        }
      ]
    });

    drawer.open(`review-${productId}`, {
      meta: {
        showCartButtonOnMobile: true,
        titleToShowOnMobileNavigation: $t('reviews.title')
      }
    });

    // wait for the drawer to on the dom
    setTimeout(() => {
      const reviewElement = document.getElementById(`review-${reviewId}`);
      if (reviewElement) {
        reviewElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 300);
  };
</script>

{#if reviews}
  {#each variant === 'small' ? reviews.slice(0, 6) : reviews as review}
    {@const images = review.images_data?.map((image) => {
      if (image.original_url) return { url: image.original_url, alt: review.title };
    }, [])}

    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div
      id={`review-${review.id}`}
      class={cn(reviewCardVariants({ variant }), className)}
      on:click={() => {
        variant === 'small' && openDrawerAndScrollToReview(productId, review.id.toString());
      }}
      on:keydown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          variant === 'small' && openDrawerAndScrollToReview(productId, review.id.toString());
        }
      }}
    >
      <div class={cn('flex items-center justify-between', variant === 'small' && 'flex-row')}>
        <div>
          <div>
            {#each Array(5) as _, i}
              <Icon
                size={variant === 'default' ? 'sm' : 'xs'}
                icon="star-filled"
                class={i < Math.round(review.score) ? 'text-gold' : 'text-gold/20'}
              />
            {/each}
          </div>
        </div>

        <p class="text-xs">
          {convertDateToLocaleString(review.created_at)}
        </p>
      </div>

      <div
        class={cn(
          variant === 'small' && 'flex flex-row gap-[10px] h-[82px] overflow-hidden',
          variant === 'default' && 'flex flex-col gap-3'
        )}
      >
        {#if review.images_data}
          <Carousel
            images={variant === 'small' && images ? [images[0]] : images}
            class={cn('mt-3', variant === 'small' && 'mt-0 w-[62px] flex-shrink-0')}
          />
        {/if}

        <div class={cn(variant === 'small' && 'flex flex-col flex-1 min-w-0 overflow-hidden')}>
          <p
            class={cn(
              'text-md font-semibold mt-3',
              variant === 'small' && 'text-sm mt-0 line-clamp-1'
            )}
          >
            {review.title}
          </p>

          <p
            class={cn(
              'mt-1',
              variant === 'small' && 'mt-[2px] text-primary/60 text-sm line-clamp-4'
            )}
          >
            {review.content}
          </p>
        </div>

        {#if review.comment && variant === 'default'}
          <div class="bg-primary/4 rounded-sm p-2.5 mt-3">
            <p class="mb-2.5 text-md">{review.comment.content}</p>
            <div class="flex items-center justify-between text-primary/60">
              <p class="text-sm mb-0">{review.comment.display_name}</p>
              <p class="text-sm mb-0">{convertDateToLocaleString(review.comment.created_at)}</p>
            </div>
          </div>
        {/if}
      </div>

      {#if review.custom_fields && variant === 'default'}
        <Divider class="mt-3" />

        <div class="flex flex-col gap-2.5 pt-2.5">
          {#each Object.values(review.custom_fields) as field}
            <div class="flex items-center justify-between text-sm/tight text-primary/60">
              <p>{field.title}</p>
              <p class="text-right">{field.value}</p>
            </div>
          {/each}
        </div>
      {/if}

      {#if review.verified_buyer}
        <div
          class={cn(
            'flex flex-col gap-4 pt-2.5',
            variant === 'small' && 'pt-0 gap-0 col-span-10 justify-end'
          )}
        >
          <div role="separator" class={cn('shrink-0 bg-primary/4 h-[1px] w-full  mb-[10px]')} />
          <div
            class={cn(
              'flex items-center justify-between text-sm/tight text-primary/60',
              variant === 'small' && 'text-sm '
            )}
          >
            <p>{review.user.display_name}</p>
            <div class="flex place-content-center">
              <Icon
                size={variant === 'default' ? 'sm' : 'xs'}
                class={cn(
                  'mr-[1px] relative bottom-[1px]',
                  variant === 'small' && 'relative bottom-0 top-[1px]'
                )}
                icon="check-circle"
              />
              <p class="text-right">{$t('reviews.verified')}</p>
            </div>
          </div>
        </div>
      {/if}
    </div>
  {/each}
{/if}
